import { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { fetchMyReservations, withdrawReservation } from '@/features/reservations/api/reservationsApi'
import { fetchFollowedProjects, unfollowProject } from '@/features/projects/api/projectsApi'
import ProjectFollowButton from '@/components/ProjectFollowButton'
import DiscountAllocationManager from '@/features/discounts/components/DiscountAllocationManager'
import { getPartnerFollows } from '@/features/partnerships/api/partnershipsApi'
import ReservationCountdown from '@/components/ReservationCountdown'
import ASPCreationWizard from '@/features/asp/components/ASPCreationWizard'
import { getASPByReservation, withdrawASP } from '@/features/asp/api/aspApi'
import ASPStatusBadge from '@/features/asp/components/ASPStatusBadge'
import ContractViewModal from '@/features/asp/components/ContractViewModal'
import LawyerShareModal from '@/features/asp/components/LawyerShareModal'
import BottomSheet from '@/components/mobile/BottomSheet'
import BottomSheetHeader from '@/components/mobile/BottomSheetHeader'
import { StatusHeader } from '@/features/buyer-dashboard/components/StatusHeader'
import { QuickStats } from '@/features/buyer-dashboard/components/QuickStatsCard'
import { ProjectHeroCard } from '@/features/buyer-dashboard/components/ProjectHeroCard'
import { EmptyState } from '@/features/buyer-dashboard/components/EmptyState'
import { ProjectUpdatesTimeline } from '@/features/buyer-dashboard/components/ProjectUpdatesTimeline'
import { UnderstandingTools } from '@/features/buyer-dashboard/components/UnderstandingTools'
import { SavedSearches } from '@/features/buyer-dashboard/components/SavedSearches'
import { PurchaseJourneyVisual } from '@/features/buyer-dashboard/components/PurchaseJourneyVisual'

function PurchaseJourneyProgress({ reservation, asp }) {
  const stages = [
    { key: 'reserved', label: 'Reserved' },
    { key: 'under_contract', label: 'Under Contract' },
    { key: 'unconditional', label: 'Unconditional' },
    { key: 'under_construction', label: 'Under Construction' },
    { key: 'ready_to_settle', label: 'Ready to Settle' },
    { key: 'settled', label: 'Settled' },
  ]

  const getCurrentStage = () => {
    if (!reservation) return 'reserved'

    if (asp) {
      if (['fully_executed'].includes(asp.status)) {
        return 'unconditional'
      }
      if (['draft', 'sent_to_lawyer', 'ready_to_sign', 'signed_by_buyer', 'signed_by_developer'].includes(asp.status)) {
        return 'under_contract'
      }
    }

    return 'reserved'
  }

  const currentStage = getCurrentStage()
  const currentIndex = stages.findIndex(s => s.key === currentStage)

  const getStageExplanation = () => {
    switch (currentStage) {
      case 'reserved':
        return {
          current: 'Your unit is reserved. You have exclusive access for the reservation period.',
          next: 'Next: Proceed to contract by signing the Agreement for Sale & Purchase.'
        }
      case 'under_contract':
        return {
          current: 'Agreement is in progress. Review and sign your contract documents.',
          next: 'Next: Once all conditions are satisfied, the agreement becomes unconditional.'
        }
      case 'unconditional':
        return {
          current: 'Agreement is fully executed and unconditional. Your purchase is confirmed.',
          next: 'Next: The developer will begin construction on your unit.'
        }
      case 'under_construction':
        return {
          current: 'Your unit is being built. You\'ll receive regular progress updates.',
          next: 'Next: Final inspections before settlement.'
        }
      case 'ready_to_settle':
        return {
          current: 'Your unit is complete and ready for settlement.',
          next: 'Next: Complete settlement and receive your keys.'
        }
      case 'settled':
        return {
          current: 'Settlement complete. Congratulations on your new home!',
          next: ''
        }
      default:
        return { current: '', next: '' }
    }
  }

  const explanation = getStageExplanation()

  return (
    <div className="border-t border-gray-100 pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">Purchase Journey</h4>
        <span className="text-xs text-gray-500">Stage {currentIndex + 1} of 6</span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {stages.map((stage, index) => (
          <div key={stage.key} className="flex items-center flex-1">
            <div
              className={`h-2 rounded-full flex-1 transition-all ${
                index <= currentIndex
                  ? 'bg-green-500'
                  : 'bg-gray-200'
              }`}
            />
            {index < stages.length - 1 && (
              <div className={`w-2 h-2 rounded-full mx-1 ${
                index < currentIndex ? 'bg-green-500' : 'bg-gray-300'
              }`} />
            )}
          </div>
        ))}
      </div>

      <div className="text-xs space-y-2">
        <div className="flex items-start gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>
          <div>
            <p className="font-semibold text-gray-900">{stages[currentIndex].label}</p>
            <p className="text-gray-600">{explanation.current}</p>
          </div>
        </div>
        {explanation.next && (
          <div className="flex items-start gap-2 pl-3.5">
            <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0"></div>
            <p className="text-gray-500">{explanation.next}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function DiscountModal({ reservation, onClose }) {
  const discount = reservation.reservation_discounts?.[0]
  if (!discount) return null

  return (
    <BottomSheet isOpen={true} onClose={onClose} height="auto">
      <BottomSheetHeader
        title="Discount Details"
        subtitle={`${reservation.unit?.project?.title} - Unit ${reservation.unit?.unit_number}`}
        onClose={onClose}
      />
      <div className="p-4 pb-8">
        <DiscountAllocationManager
          reservationId={reservation.id}
          projectId={reservation.unit?.project?.id}
          unitPrice={reservation.unit?.price}
          discountData={discount}
        />

        <button
          onClick={onClose}
          className="w-full mt-4 px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 font-medium touch-target"
        >
          Close
        </button>
      </div>
    </BottomSheet>
  )
}

function AgreementDetailsModal({ reservation, asp, onClose, onViewContract, onShareWithLawyer, onDelete }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (!asp) return null

  const handleDelete = async () => {
    setDeleting(true)
    await onDelete()
    setDeleting(false)
  }

  return (
    <BottomSheet isOpen={true} onClose={onClose} height="auto">
      <BottomSheetHeader
        title="Agreement Details"
        subtitle={asp.status ? `Status: ${asp.status}` : undefined}
        onClose={onClose}
      />
      <div className="p-4 pb-8">
        <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Agreement ID</p>
              <p className="font-mono text-sm text-gray-900">#{asp.id.slice(0, 8)}</p>
            </div>
            <ASPStatusBadge status={asp.status} />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 mb-1">Purchase Price</p>
                <p className="font-semibold text-gray-900">
                  ${parseFloat(asp.purchase_price_nzd).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Deposit</p>
                <p className="font-semibold text-gray-900">
                  ${parseFloat(asp.deposit_amount_nzd).toLocaleString()}
                </p>
              </div>
              {asp.final_discount_applied && parseFloat(asp.final_discount_applied) > 0 && (
                <div>
                  <p className="text-gray-500 mb-1">Discount Applied</p>
                  <p className="font-semibold text-green-700">
                    -${parseFloat(asp.final_discount_applied).toLocaleString()}
                  </p>
                </div>
              )}
              <div>
                <p className="text-gray-500 mb-1">Sunset Date</p>
                <p className="font-semibold text-gray-900">
                  {new Date(asp.sunset_date).toLocaleDateString('en-NZ')}
                </p>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-600 mb-6">
            <p className="mb-1">
              <strong>Project:</strong> {reservation.unit?.project?.title}
            </p>
            <p>
              <strong>Unit:</strong> {reservation.unit?.unit_number}
            </p>
          </div>

          {asp.buyer_signed_at && (
            <div className="mb-4 flex items-center gap-2 text-green-700 text-sm bg-green-50 px-3 py-2 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>You have signed this agreement</span>
            </div>
          )}

          {asp.sent_to_lawyer_at && (
            <div className="mb-4 flex items-center gap-2 text-blue-700 text-sm bg-blue-50 px-3 py-2 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Shared with lawyer on {new Date(asp.sent_to_lawyer_at).toLocaleDateString('en-NZ')}</span>
            </div>
          )}

          {!showDeleteConfirm ? (
            <div className="space-y-3">
              <button
                onClick={onViewContract}
                className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Agreement
              </button>

              {!asp.sent_to_lawyer_at && !asp.buyer_signed_at && (
                <button
                  onClick={onShareWithLawyer}
                  className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Forward to Lawyer
                </button>
              )}

              {!asp.buyer_signed_at && asp.status !== 'withdrawn' && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full px-4 py-2.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Agreement
                </button>
              )}
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 mb-4">
                Are you sure you want to delete this agreement? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          )}
      </div>
    </BottomSheet>
  )
}

function FollowedProjectCard({ project, reservation, asp, onUnfollow, onProceedToContract, onWithdraw, onViewAgreement }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const images = project.image_urls?.filter(Boolean) || []
  const hasMultipleImages = images.length > 1
  const isContracted = !!reservation && reservation.status === 'active'

  const hasDiscount = reservation?.reservation_discounts &&
    Array.isArray(reservation.reservation_discounts) &&
    reservation.reservation_discounts.length > 0 &&
    reservation.reservation_discounts[0]?.locked_dollar_amount

  const calculateDiscountedPrice = () => {
    if (!hasDiscount || !reservation) return reservation?.unit?.price
    const allocation = reservation.reservation_discounts[0].discount_allocations?.[0]
    if (allocation) {
      const priceReduction = parseFloat(allocation.price_reduction || 0)
      const unallocated = parseFloat(allocation.unallocated || 0)
      return reservation.unit.price - priceReduction - unallocated
    }
    return reservation?.unit?.price
  }

  const discountAmount = hasDiscount
    ? parseFloat(reservation.reservation_discounts[0].locked_dollar_amount)
    : 0

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) {
      return
    }
    window.location.href = `/projects/${project.id}`
  }

  return (
    <>
      <div
        onClick={handleCardClick}
        className="bg-white overflow-hidden transition-all cursor-pointer border-b md:border-b-0 md:rounded-xl md:border md:border-gray-200 md:shadow-sm md:hover:shadow-md"
      >
        <div className="flex flex-col">
          <div className="relative w-full aspect-[16/9] bg-gray-200 overflow-hidden">
            {images.length > 0 ? (
              <img
                src={images[currentImageIndex]}
                alt={project.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            )}

            {hasMultipleImages && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setCurrentImageIndex(index)
                    }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex
                        ? 'bg-white w-6'
                        : 'bg-white/60 hover:bg-white/80'
                    }`}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            )}

            <div className="absolute top-4 right-4 z-10">
              <ProjectFollowButton
                isFollowing={true}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onUnfollow(project.id)
                }}
                size="md"
              />
            </div>

            {isContracted && (
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-semibold">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Contracted
                </span>
              </div>
            )}
          </div>

          <div className="p-4">
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsExpanded(!isExpanded)
              }}
              className="w-full flex items-start justify-between gap-4 text-left"
            >
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {project.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {project.location}
                </p>
                {project.price_range_min && project.price_range_max && !isContracted && (
                  <p className="text-base font-semibold text-blue-600 mt-2">
                    ${project.price_range_min.toLocaleString()} - ${project.price_range_max.toLocaleString()}
                  </p>
                )}
              </div>
              <svg
                className={`w-5 h-5 text-gray-600 transition-transform flex-shrink-0 mt-1 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isExpanded && isContracted && reservation && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">Unit {reservation.unit?.unit_number}</span> - {reservation.unit?.bedrooms} bed, {reservation.unit?.bathrooms} bath, {reservation.unit?.sqm} sqm
                  </p>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    {hasDiscount ? (
                      <>
                        <span className="font-bold text-green-700">
                          ${calculateDiscountedPrice().toLocaleString('en-NZ')}
                        </span>
                        <span className="text-gray-400 line-through text-xs">
                          ${reservation.unit.price.toLocaleString('en-NZ')}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowDiscountModal(true)
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-semibold hover:bg-green-200 transition-colors"
                        >
                          Save ${discountAmount.toLocaleString('en-NZ')}
                        </button>
                      </>
                    ) : (
                      <span className="font-bold text-gray-900">
                        ${reservation.unit?.price?.toLocaleString('en-NZ')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-3 mb-4">
                  <ReservationCountdown
                    expiresAt={reservation.expires_at}
                    isReducedDuration={reservation.is_reduced_duration}
                  />

                  <div className="flex flex-col sm:flex-row items-center gap-2">
                    {!reservation.asp_initiated_at ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onProceedToContract(reservation)
                        }}
                        className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Continue to Contract
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onViewAgreement(reservation)
                        }}
                        className="w-full px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Agreement
                      </button>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onWithdraw(reservation)
                      }}
                      className="w-full px-4 py-2.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Withdraw
                    </button>
                  </div>
                </div>

                <PurchaseJourneyProgress reservation={reservation} asp={asp} />
              </div>
            )}
          </div>
        </div>
      </div>

      {showDiscountModal && reservation && (
        <DiscountModal
          reservation={reservation}
          onClose={() => setShowDiscountModal(false)}
        />
      )}
    </>
  )
}

export default function BuyerDashboardPage() {
  const { profile } = useAuth()
  const [showASPWizard, setShowASPWizard] = useState(false)
  const [selectedReservation, setSelectedReservation] = useState(null)
  const [viewingContract, setViewingContract] = useState(null)
  const [withdrawConfirm, setWithdrawConfirm] = useState(null)
  const [agreementModalReservation, setAgreementModalReservation] = useState(null)
  const [agreementModalASP, setAgreementModalASP] = useState(null)
  const [loadingASP, setLoadingASP] = useState(false)
  const [showLawyerShare, setShowLawyerShare] = useState(false)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [asps, setAsps] = useState({})
  const queryClient = useQueryClient()

  if (profile && profile.role !== 'buyer') {
    return <Navigate to="/" replace />
  }

  const { data: reservationsData, isLoading: reservationsLoading } = useQuery({
    queryKey: ['my-reservations'],
    queryFn: () => fetchMyReservations(),
  })

  const { data: followedData, isLoading: followedLoading } = useQuery({
    queryKey: ['followed-projects'],
    queryFn: () => fetchFollowedProjects(),
  })

  const withdrawMutation = useMutation({
    mutationFn: withdrawReservation,
    onSuccess: () => {
      queryClient.invalidateQueries(['my-reservations'])
    },
  })

  const unfollowMutation = useMutation({
    mutationFn: unfollowProject,
    onSuccess: () => {
      queryClient.invalidateQueries(['followed-projects'])
    },
  })

  const reservations = reservationsData?.data || []
  const activeReservations = reservations.filter(r => r.status === 'active')
  const followedProjects = followedData?.data || []

  const reservationsByProject = {}
  activeReservations.forEach(reservation => {
    const projectId = reservation.unit?.project?.id
    if (projectId) {
      reservationsByProject[projectId] = reservation
    }
  })

  useEffect(() => {
    const fetchAllAsps = async () => {
      const aspData = {}

      for (const reservation of activeReservations) {
        if (reservation.asp_initiated_at) {
          try {
            const asp = await getASPByReservation(reservation.id)
            if (asp) {
              aspData[reservation.id] = asp
            }
          } catch (err) {
            console.error(`Error fetching ASP for reservation ${reservation.id}:`, err)
          }
        }
      }

      setAsps(aspData)
    }

    if (activeReservations.length > 0) {
      fetchAllAsps()
    }
  }, [activeReservations.length])

  const handleWithdraw = (reservation) => {
    setWithdrawConfirm(reservation)
  }

  const confirmWithdraw = () => {
    if (withdrawConfirm) {
      withdrawMutation.mutate(withdrawConfirm.id)
      setWithdrawConfirm(null)
    }
  }

  const handleUnfollow = (projectId) => {
    const hasReservation = reservationsByProject[projectId]
    if (hasReservation) {
      alert('You cannot unfollow a project you have contracted. Please withdraw your reservation first.')
      return
    }
    if (window.confirm('Unfollow this project?')) {
      unfollowMutation.mutate(projectId)
    }
  }

  const handleProceedToContract = (reservation) => {
    setSelectedReservation(reservation)
    setShowASPWizard(true)
  }

  const handleViewAgreement = async (reservation) => {
    setLoadingASP(true)
    try {
      const asp = await getASPByReservation(reservation.id)
      if (asp) {
        setAgreementModalReservation(reservation)
        setAgreementModalASP(asp)
      }
    } catch (err) {
      console.error('Error fetching ASP:', err)
    } finally {
      setLoadingASP(false)
    }
  }

  const handleDeleteAgreement = async () => {
    if (!agreementModalASP) return
    try {
      await withdrawASP(agreementModalASP.id, 'Deleted by buyer')
      queryClient.invalidateQueries(['my-reservations'])
      setAgreementModalReservation(null)
      setAgreementModalASP(null)
    } catch (err) {
      console.error('Error deleting ASP:', err)
    }
  }

  const handleASPComplete = () => {
    setShowASPWizard(false)
    setSelectedReservation(null)
    queryClient.invalidateQueries(['my-reservations'])
  }

  const isLoading = followedLoading || reservationsLoading

  const firstName = profile?.full_name?.split(' ')[0] || null

  return (
    <div className="bg-neutral-50 min-h-screen">
      <StatusHeader
        userName={firstName}
        followingCount={followedProjects.length}
        reservedCount={activeReservations.length}
        hasUnreadNotifications={false}
        actionRequired={activeReservations.some(r =>
          new Date(r.expires_at) < new Date(Date.now() + 24 * 60 * 60 * 1000) && !r.asp_initiated_at
        )}
      />

      <div className="container-custom py-6 md:py-8">
        <QuickStats
          followingCount={followedProjects.length}
          reservedCount={activeReservations.length}
        />

        {profile?.buyer_category === 'first_home' && activeReservations.length === 0 && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-green-900 mb-1">First Home Buyer Tip</h3>
                <p className="text-sm text-green-800">
                  When you reserve a unit, you may be eligible for up to $10,000 towards your deposit.
                  Look for projects with active discounts to maximize your savings.
                </p>
              </div>
            </div>
          </div>
        )}

        {profile?.buyer_intent === 'investment' && activeReservations.length === 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Investor Tip</h3>
                <p className="text-sm text-blue-800">
                  We show yield calculations on unit pages to help you evaluate returns.
                  Consider applying your full discount to price reduction for better rental yields.
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <EmptyState type="loading" />
        ) : followedProjects.length === 0 ? (
          <EmptyState type="no-projects" />
        ) : (
          <>
            <div className="space-y-0 md:space-y-6 -mx-4 md:mx-0 mb-8">
              {followedProjects.map((follow) => (
                <ProjectHeroCard
                  key={follow.id}
                  project={follow.project}
                  reservation={reservationsByProject[follow.project.id]}
                  asp={reservationsByProject[follow.project.id] ? asps[reservationsByProject[follow.project.id].id] : null}
                  onUnfollow={handleUnfollow}
                  onProceedToContract={handleProceedToContract}
                  onWithdraw={handleWithdraw}
                  onViewAgreement={handleViewAgreement}
                  onViewDiscount={(reservation) => {
                    setSelectedReservation(reservation)
                    setShowDiscountModal(true)
                  }}
                />
              ))}
            </div>

            {activeReservations.length > 0 && (
              <div className="mb-8">
                <PurchaseJourneyVisual
                  currentStage={
                    activeReservations[0]?.asp_initiated_at
                      ? asps[activeReservations[0]?.id]?.status === 'fully_executed'
                        ? 'unconditional'
                        : 'under_contract'
                      : 'reserved'
                  }
                />
              </div>
            )}

            <div className="mb-8">
              <ProjectUpdatesTimeline
                projectIds={followedProjects.map(f => f.project.id)}
              />
            </div>

            <div className="mb-8">
              <UnderstandingTools />
            </div>

            <div className="mb-8">
              <SavedSearches />
            </div>
          </>
        )}

        {showASPWizard && selectedReservation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Agreement for Sale & Purchase
                </h2>
                <button
                  onClick={() => {
                    setShowASPWizard(false)
                    setSelectedReservation(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <ASPCreationWizard
                  reservation={selectedReservation}
                  unit={selectedReservation.unit}
                  project={selectedReservation.unit?.project}
                  buyer={profile}
                  onComplete={handleASPComplete}
                />
              </div>
            </div>
          </div>
        )}

        {agreementModalASP && agreementModalReservation && (
          <AgreementDetailsModal
            reservation={agreementModalReservation}
            asp={agreementModalASP}
            onClose={() => {
              setAgreementModalReservation(null)
              setAgreementModalASP(null)
            }}
            onViewContract={() => {
              setViewingContract(agreementModalASP)
              setAgreementModalReservation(null)
              setAgreementModalASP(null)
            }}
            onShareWithLawyer={() => {
              setShowLawyerShare(true)
            }}
            onDelete={handleDeleteAgreement}
          />
        )}

        {viewingContract && (
          <ContractViewModal
            asp={viewingContract}
            onClose={() => setViewingContract(null)}
          />
        )}

        {showLawyerShare && agreementModalASP && (
          <LawyerShareModal
            aspId={agreementModalASP.id}
            onClose={() => setShowLawyerShare(false)}
            onSuccess={() => {
              setShowLawyerShare(false)
              queryClient.invalidateQueries(['my-reservations'])
            }}
          />
        )}

        {showDiscountModal && selectedReservation && (
          <DiscountModal
            reservation={selectedReservation}
            onClose={() => {
              setShowDiscountModal(false)
              setSelectedReservation(null)
            }}
          />
        )}

        {withdrawConfirm && (
          <BottomSheet isOpen={true} onClose={() => setWithdrawConfirm(null)} height="auto">
            <BottomSheetHeader
              title="Withdraw Reservation?"
              subtitle={`Unit ${withdrawConfirm.unit?.unit_number} - ${withdrawConfirm.unit?.project?.title}`}
              onClose={() => setWithdrawConfirm(null)}
            />
            <div className="p-4 pb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-error-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-error-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-secondary-600 text-sm">
                    You are about to withdraw your reservation for{' '}
                    <span className="font-medium">Unit {withdrawConfirm.unit?.unit_number}</span> at{' '}
                    <span className="font-medium">{withdrawConfirm.unit?.project?.title}</span>.
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-amber-800 mb-1">
                      Important
                    </p>
                    <p className="text-sm text-amber-700">
                      {withdrawConfirm.is_reduced_duration
                        ? 'Your future reservations will continue to be limited to 48 hours.'
                        : 'After withdrawing, any future reservations will only be for 48 hours instead of 72 hours.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setWithdrawConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Keep Reservation
                </button>
                <button
                  onClick={confirmWithdraw}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  disabled={withdrawMutation.isPending}
                >
                  {withdrawMutation.isPending ? 'Withdrawing...' : 'Yes, Withdraw'}
                </button>
              </div>
            </div>
          </BottomSheet>
        )}

        {loadingASP && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center gap-3">
              <div className="spinner spinner-md text-blue-600"></div>
              <span className="text-gray-700">Loading agreement...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
